import classNames from "classnames";
import React from "react";
import "./kuika-drawn-check.scss";

/** `check` — the tick a selection draws · `dash` — the "some, not all" bar of a partial one. */
export type KuikaDrawnCheckGlyph = "check" | "dash";

// Drawn on a 16×16 box, as one open stroke each so `kuika-draw` has a single path to run along.
const GLYPH_PATHS: Record<KuikaDrawnCheckGlyph, string> = {
  check: "M3.5 8.5l3 3 6-7",
  dash: "M4 8h8"
};

export interface IKuikaDrawnCheckProps {
  glyph?: KuikaDrawnCheckGlyph;
  /**
   * Draw the stroke in now, rather than showing it whole. The caller decides what "now" means: an
   * upload row that arrives already finished draws on mount (`draw` is simply on), while a filter
   * chip or a checkbox draws only the glyph the last change turned on, so a list opening with fifty
   * ticks does not draw fifty ticks.
   */
  draw?: boolean;
  /**
   * Width and height in px, written as SVG attributes rather than inline styles, so a stylesheet of
   * the consumer's still decides the size. Leave it out when the consumer sizes the glyph in CSS.
   */
  size?: number;
  className?: string;
}

/**
 * A stroked ✓ (or "some, not all" bar) that draws itself along its own length.
 *
 * This is the kit's one sanctioned inline `<svg>`: `kuika-draw` animates `stroke-dashoffset`, and a
 * `KuikaSvg` renders its asset as a CSS mask, which has no stroke to animate. Everything else —
 * size, colour, stroke weight, and how long the draw is held back — comes from the consumer's
 * stylesheet (`--kuika-drawn-check-color`, `--kuika-drawn-check-stroke`, `--kuika-drawn-check-delay`).
 *
 * Always `aria-hidden`: it decorates a control that already says what it means (a pressed chip, a
 * checked box, a row marked done).
 */
const KuikaDrawnCheck = ({ glyph = "check", draw = false, size, className }: IKuikaDrawnCheckProps): JSX.Element => (
  <svg
    className={classNames("kuika-drawn-check", { "kuika-drawn-check--draw": draw }, className)}
    viewBox="0 0 16 16"
    width={size}
    height={size}
    aria-hidden="true"
    focusable="false"
  >
    <path pathLength="24" d={GLYPH_PATHS[glyph]} />
  </svg>
);

export default KuikaDrawnCheck;
